import gql from "graphql-tag";
import * as React from "react";

import type {Result} from "@khanacademy/wonder-blocks-data";
import {useMutation} from "@repo/data-gql";
import {gqlOp, type GqlOperation} from "@repo/data-gql-core";
import {Errors, KAError} from "@repo/logging";

import type {ComputingCodeMultiFile} from "#content-library/types/types.ts";

import type {
    createPythonProgram as GqlResponse,
    createPythonProgramVariables as GqlVariables,
} from "./__graphql-types__/createPythonProgram.ts";
import {pyIconUrl} from "./thumbnail-icon.ts";

const defaultUserProgramCode: ComputingCodeMultiFile = {
    files: [
        {
            code: "",
            filename: "main.py",
            language: "python",
        },
    ],
};
const defaultUserProgramJSON = JSON.stringify(defaultUserProgramCode);

export const createProgramMutation: GqlOperation<GqlResponse, GqlVariables> =
    gqlOp(gql`
        mutation createPythonProgram(
            $title: String!
            $revision: ProgramRevisionInput!
            $curationNodeSlug: String!
        ) {
            createProgram(
                title: $title
                revision: $revision
                curationNodeSlug: $curationNodeSlug
                userAuthoredContentType: PYTHON
                codeFormat: MULTI_FILE_JSON
            ) {
                program {
                    id
                    url
                }
                error {
                    code
                    debugMessage
                }
            }
        }
    `);

type CreatePythonProgramFn = (_: {
    title: string;
    curationNodeSlug: string;
}) => Promise<Result<{url: string}>>;

export function useCreatePythonProgram(): {
    createPythonProgram: CreatePythonProgramFn;
    result: Result<{url: string}> | null | undefined;
} {
    const [createProgram, result] = useMutation(createProgramMutation);
    const createPythonProgram = React.useCallback<CreatePythonProgramFn>(
        async ({title, curationNodeSlug}) => {
            const result = await createProgram({
                title,
                curationNodeSlug,
                revision: {
                    code: defaultUserProgramJSON,
                    configVersion: 0,
                    folds: [],
                    imageUrl: pyIconUrl,
                },
            });

            return mapGqlResult(result);
        },
        [createProgram],
    );
    return {
        createPythonProgram,
        result: result == null ? null : mapGqlResult(result),
    };
}

function mapGqlResult(result: Result<GqlResponse>): Result<{url: string}> {
    if (result.status !== "success") {
        return result;
    }
    if (result.data.createProgram?.program?.url == null) {
        return {
            status: "error",
            error: new KAError(
                "failed to create Python user program",
                Errors.Internal,
            ),
        };
    }
    return {
        status: "success",
        data: {url: result.data.createProgram.program.url},
    };
}
